import React from 'react';
import PropTypes from 'prop-types';
import Assets from '../../../../../requests/apa/Asset';
import './_MatchDetailLineupsPlayer.scss';

/**
 * MatchDetailLineupsPlayer — Large/Jugadores (node 106:11182)
 *
 * Card de jugador posicionado sobre el campo.
 * Sin flex ni grid. Layout con position: absolute interior.
 *
 * Conversión px→rem: (px / 1.5) / 10
 * Card: 129×88px → 8.6rem × 5.867rem
 *
 * Slots de iconos:
 *   TL (top-left):  eventos que van arriba-izquierda (sub_out, red)
 *   TR (top-right): eventos que van arriba-derecha (tarjeta)
 *   BL (bottom-left):  flecha de entrada / gol
 *   BR (bottom-right): número de gol / info extra
 */

const IconYellowCard = () => (
  <span className="lineups-player__card lineups-player__card--yellow" />
);

const IconRedCard = () => (
  <span className="lineups-player__card lineups-player__card--red" />
);

const IconRedSecondYellow = () => (
  <span className="lineups-player__double-card">
    <span className="lineups-player__double-card-inner lineups-player__double-card-inner--yellow" />
    <span className="lineups-player__double-card-inner lineups-player__double-card-inner--red" />
  </span>
);

const IconArrowIn = () => (
  <svg
    className="lineups-player__arrow-svg"
    viewBox="0 0 10 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M5 0L10 6H6V12H4V6H0L5 0Z" fill="#4CAF50" />
  </svg>
);

const IconArrowOut = () => (
  <svg
    className="lineups-player__arrow-svg"
    viewBox="0 0 10 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M5 12L0 6H4V0H6V6H10L5 12Z" fill="#F44336" />
  </svg>
);

const IconGoal = () => (
  <svg
    className="lineups-player__arrow-svg"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="7" stroke="#ffffff" strokeWidth="1.5" />
    <path d="M8 2L9.5 6H14L10.5 8.5L12 12.5L8 10L4 12.5L5.5 8.5L2 6H6.5L8 2Z" fill="#ffffff" />
  </svg>
);

/**
 * CanchaImg — <img> con URL de icono Cancha desde el mock.
 * Reemplaza los iconos SVG inline cuando iconUrls está disponible.
 * Tipos: Cancha Page-1 (tarjeta) | Cancha Group31433 (sub flecha)
 */
var CanchaImg = function (props) {
  return (
    <img
      className="lineups-player__cancha-img"
      src={props.src}
      alt=""
    />
  );
};

/**
 * CanchaImgDouble — Cancha Group Clip path group Group
 * Representa la doble flecha sub_in+sub_out combinadas (un icono 24x24).
 * Se renderiza como dos <img> apiladas: verde arriba (in) + roja abajo (out).
 */
var CanchaImgDouble = function (props) {
  return (
    <span className="lineups-player__cancha-double">
      <img className="lineups-player__cancha-img lineups-player__cancha-img--in" src={props.inUrl} alt="" />
      <img className="lineups-player__cancha-img lineups-player__cancha-img--out" src={props.outUrl} alt="" />
    </span>
  );
};

/**
 * Devuelve los iconos para los slots TL, TR, BL, BR
 * según el array de eventos del jugador.
 *
 * Si el jugador tiene `iconUrls` en el mock, usa <CanchaImg> (<img>) en lugar
 * del SVG inline. Para sub_in+sub_out juntos, coloca la doble flecha
 * (Cancha Group Clip path group Group) en el slot BR.
 *
 * Slots:
 *   TL: sub_out
 *   TR: yellow / red / red_second_yellow
 *   BL: sub_in / goal
 *   BR: second yellow card | doble flecha (sub_in+sub_out)
 */
function resolveIcons(events, iconUrls) {
  var slots = { tl: null, tr: null, bl: null, br: null, yr: null };
  if (!events || events.length === 0) return slots;

  var urls = iconUrls || {};
  var hasSubIn  = events.indexOf('sub_in')  !== -1;
  var hasSubOut = events.indexOf('sub_out') !== -1;

  // sub_in + sub_out: mostrar cada flecha en su slot propio (sin doble icono)
  if (hasSubIn && hasSubOut) {
    slots.tl = urls.sub_out
      ? <CanchaImg src={urls.sub_out} />
      : <IconArrowOut />;
    slots.bl = urls.sub_in
      ? <CanchaImg src={urls.sub_in} />
      : <IconArrowIn />;
  } else if (hasSubOut) {
    slots.tl = urls.sub_out
      ? <CanchaImg src={urls.sub_out} />
      : <IconArrowOut />;
  } else if (hasSubIn) {
    slots.bl = urls.sub_in
      ? <CanchaImg src={urls.sub_in} />
      : <IconArrowIn />;
  } 


  events.forEach(function (event) {
    switch (event) {
      case 'yellow':
        if (!slots.tr) {
          slots.tr = urls.yellow
            ? <CanchaImg src={urls.yellow} />
            : <IconYellowCard />;
        } else if (!slots.br) {
          slots.br = urls.yellow
            ? <CanchaImg src={urls.yellow} />
            : <IconYellowCard />;
        }
        break;
      case 'red':
        slots.tr = urls.red
          ? <CanchaImg src={urls.red} />
          : <IconRedCard />;
        break;
      case 'red_second_yellow':
        slots.yr = urls.red_second_yellow
          ? <CanchaImg src={urls.red_second_yellow} />
          : <IconRedSecondYellow />;
        break;
      case 'goal':
      case 'own_goal':
        if (!slots.bl) {
          slots.bl = urls.goal
            ? <CanchaImg src={urls.goal} />
            : <IconGoal />;
        }
        break;
      default:
        break;
    }
  });

  return slots;
}

const MatchDetailLineupsPlayer = ({ player, teamColor }) => {
  const icons = resolveIcons(player.events, player.iconUrls);
  const borderColor = teamColor || '#89c7e2';
  const photoModifier = teamColor === '#fe0100'
    ? 'lineups-player__photo-wrap--away'
    : 'lineups-player__photo-wrap--home';
 // Extraer solo el apellido (última palabra) del nombre
  const getLastName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1] : fullName;
  };

  const displayName = getLastName(player.name);


  return (
    <div className="lineups-player">
      {/* Foto del jugador */}
      <div
        className={'lineups-player__photo-wrap ' + photoModifier}
        style={{ borderColor: borderColor }}
      >
          <img
            src={player.photoUrl || Assets.get('eventodeportivo_placeholder_imagen_jugador_2')}
            alt=""
            className="match-detail-lineups-top-bar__lineups-player__photo"
            onError={function(e) {
              e.target.onerror = null;
              e.target.src = Assets.get('eventodeportivo_placeholder_imagen_jugador_2');
            }}
          />
      </div>

      {/* Nombre */}
      <span className="lineups-player__name">{displayName}</span>

      {/* Iconos — slot top-right FLECHA VERDE Y ROJA, correcta */}
      {icons.tl && (
        <span className="lineups-player__icon lineups-player__icon--tl">
          {icons.tl}
        </span>
      )}

      {/* Iconos — Iconos — slot bottom-left TARJETA ROJA */}
      {icons.br && (
        <span className="lineups-player__icon lineups-player__icon--tr">
               {icons.br}
        </span>
      )}

      {/* Iconos — slot bottom-left TARJETA AMARILLA */}
      {icons.tr && (
        <span className="lineups-player__icon lineups-player__icon--br">
          {icons.tr}
        </span>
      )}

      {/* Iconos — slot bottom-left BALON gol*/}
      {icons.bl && (
        <span className="lineups-player__icon lineups-player__icon--bl">
          {icons.bl}
          {player.goals > 1 && (
            <span className="lineups-player__goals-count">{player.goals}</span>
          )}
        </span>
      )}

      {/* Iconos — slot top-right FLECHA VERDE Y ROJA, correcta */}
      {icons.yr && (
        <span className="lineups-player__icon lineups-player__icon--yr">
          {icons.yr}
        </span>
      )}

    </div>
  );
};

MatchDetailLineupsPlayer.propTypes = {
  player: PropTypes.shape({
    id: PropTypes.number,
    number: PropTypes.number,
    name: PropTypes.string.isRequired,
    photoUrl: PropTypes.string,
    x: PropTypes.number,
    y: PropTypes.number,
    events: PropTypes.arrayOf(PropTypes.string),
    iconUrls: PropTypes.objectOf(PropTypes.string),
    goals: PropTypes.number
  }).isRequired,
  teamColor: PropTypes.string
};

MatchDetailLineupsPlayer.defaultProps = {
  teamColor: '#89c7e2'
};

export default MatchDetailLineupsPlayer;



// WEBPACK FOOTER //
// ./src/containers/Mundial2026/FootballMatchDetail/Components/MatchDetailLineupsPlayer/MatchDetailLineupsPlayer.jsx